import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import PaymentFormFields from "@/components/forms/PaymentFormFields";
import CustomerDetailsForm from "@/components/forms/CustomerDetailsForm";
import PaymentOptionsForm from "@/components/forms/PaymentOptionsForm";
import PaymentActionButtons from "@/components/forms/PaymentActionButtons";

import { PaymentFormData } from "@/types/PaymentFormData";
import { useBusinessProfile } from "@/hooks/useBusinessProfile";
import { usePaymentFormState } from "@/hooks/usePaymentFormState";
import { useEmailPaymentHandler } from "@/components/forms/EmailPaymentHandler";
import { useSmsPaymentHandler } from "@/components/forms/SmsPaymentHandler";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { createOrUpdateCustomer } from "@/utils/customerUtils";
import { getDocumentMeta, type DocumentType } from "@/utils/documentMeta";
import SmsQuotaDialog from "@/components/customers/SmsQuotaDialog";
import SmsQuotaBadge from "@/components/forms/SmsQuotaBadge";
import { useSmsQuota } from "@/hooks/useSmsQuota";
import { notifySmsQuotaExceeded } from "@/utils/smsQuotaNotify";
import { usePaymentSettings } from "@/hooks/usePaymentSettings";

const formSchema = z.object({
  amount: z.string().min(1, { message: "Amount is required." })
    .refine((val) => parseFloat(val) > 0, { message: "Amount must be greater than 0." }),
  currency: z.string().min(1, { message: "Currency is required." }),
  description: z.string().optional(),
  customerEmail: z.string().email({ message: "Please enter a valid email address." }).optional().or(z.literal("")),
  customerPhone: z.string().optional(),
  customerName: z.string().min(1, { message: "Customer name is required." }),
  includeReviewLink: z.boolean().default(false).optional(),
  selectedReviewLink: z.string().optional(),
  includeAddress: z.boolean().default(false).optional(),
  includePdfReceipt: z.boolean().default(false).optional(),
  street: z.string().optional(),
  city: z.string().optional(),
  postcode: z.string().optional(),
  country: z.string().optional(),
}).refine(
  (data) => (data.customerEmail && data.customerEmail.length > 0) || (data.customerPhone && data.customerPhone.length > 0),
  { message: "Either email or phone number is required", path: ["customerEmail"] }
);

const PaymentLinkForm = ({ onLinkGenerated }: { onLinkGenerated?: () => void }) => {
  const navigate = useNavigate();
  const { businessProfile } = useBusinessProfile();
  const { user } = useAuth();
  const { toast } = useToast();
  const [sendingType, setSendingType] = useState<DocumentType | null>(null);
  const [watchIncludePdf, setWatchIncludePdf] = useState(false);
  const [watchPaidWithCash, setWatchPaidWithCash] = useState(false);
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);
  const [pendingEmailFallback, setPendingEmailFallback] = useState<null | (() => Promise<void>)>(null);
  const { quota } = useSmsQuota();
  const {
    watchIncludeReviewLink,
    setWatchIncludeReviewLink,
    watchIncludeAddress,
    setWatchIncludeAddress,
    selectedReviewPlatform,
    setSelectedReviewPlatform,
  } = usePaymentFormState();

  const form = useForm<PaymentFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      amount: "",
      currency: "GBP",
      description: "",
      customerEmail: "",
      customerPhone: "",
      customerName: "",
      includeReviewLink: false,
      selectedReviewLink: "",
      includeAddress: false,
      includePdfReceipt: false,
      street: "",
      city: "",
      postcode: "",
      country: "",
    },
  });

  const { handleSendPaymentEmail } = useEmailPaymentHandler({
    form,
    businessProfile,
    watchIncludeReviewLink,
    watchIncludeAddress,
    selectedReviewPlatform,
    onLinkGenerated,
  });

  const { handleSendPaymentSms } = useSmsPaymentHandler({
    form,
    businessProfile,
    watchIncludeReviewLink,
    watchIncludeAddress,
    selectedReviewPlatform,
    onLinkGenerated,
  });

  const handleReviewLinkSelected = (platform: string) => {
    setSelectedReviewPlatform(platform);
    form.setValue("selectedReviewLink", platform);
  };

  const { settings: paymentSettings } = usePaymentSettings();
  const smsDisabled = !!paymentSettings.smsDisabled;
  const customerEmail = form.watch("customerEmail");
  const customerPhone = form.watch("customerPhone");
  const hasEmail = !!(customerEmail && customerEmail.trim().length > 0);
  const hasPhone = !smsDisabled && !!(customerPhone && customerPhone.trim().length > 0);

  const formCardRef = useRef<HTMLDivElement | null>(null);

  // Allow other components (e.g. estimate customer cards) to prefill this
  // form via a custom window event and scroll it into view.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      const hasAddress = !!(detail.street || detail.city || detail.postcode || detail.country);
      form.reset({
        amount: detail.amount ? String(detail.amount) : "",
        currency: detail.currency || "GBP",
        description: detail.description || "",
        customerEmail: detail.customerEmail || "",
        customerPhone: detail.customerPhone || "",
        customerName: detail.customerName || "",
        includeAddress: hasAddress,
        includePdfReceipt: false,
        includeReviewLink: false,
        selectedReviewLink: "",
        street: detail.street || "",
        city: detail.city || "",
        postcode: detail.postcode || "",
        country: detail.country || "",
      });
      setWatchIncludeAddress(hasAddress);
      setWatchPaidWithCash(false);
      setTimeout(() => {
        formCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    };
    window.addEventListener("prefillPaymentForm", handler as EventListener);
    try {
      const stashed = sessionStorage.getItem("prefillPaymentForm");
      if (stashed) {
        sessionStorage.removeItem("prefillPaymentForm");
        handler(new CustomEvent("prefillPaymentForm", { detail: JSON.parse(stashed) }));
      }
    } catch (e) {
      console.error("Failed to read prefill data", e);
    }
    return () => window.removeEventListener("prefillPaymentForm", handler as EventListener);
  }, [form, setWatchIncludeAddress]);


  const onSend = async (requestedDocType: DocumentType) => {
    const ok = await form.trigger();
    if (!ok) return;
    const data = form.getValues();
    if (!hasEmail && !hasPhone) {
      toast({
        title: "Missing contact",
        description: smsDisabled
          ? "SMS is disabled — add a customer email to send."
          : "Add a customer email or phone number first.",
        variant: "destructive",
      });
      return;
    }

    // "Paid With Cash" only applies to the Receipt button.
    const docType: DocumentType = requestedDocType;
    const paymentMethod =
      docType === "receipt" && watchPaidWithCash ? "Cash" : undefined;
    const customerPaymentStatus =
      docType === "estimate" ? "estimate" :
      docType === "receipt" ? "paid" :
      undefined;

    setSendingType(requestedDocType);
    try {
      const meta = getDocumentMeta(docType);
      const sharedPaymentId = crypto.randomUUID();

      const includePdf = docType === "invoice" ? watchIncludePdf : true;
      const willSendBoth = hasEmail && hasPhone;

      if (willSendBoth) {
        await Promise.all([
          handleSendPaymentEmail({ paymentId: sharedPaymentId, skipPersistence: true, documentType: docType, includePdf, paymentMethod, customerPaymentStatus }),
          handleSendPaymentSms({ paymentId: sharedPaymentId, skipPersistence: true, documentType: docType, includePdf, paymentMethod, customerPaymentStatus }),
        ]);

        const amount = parseFloat(data.amount || "0");
        const description = data.description || "";
        const currency = data.currency || "GBP";

        const { error: paymentError } = await supabase.from("payment_links").insert([{
          id: sharedPaymentId,
          user_id: user?.id,
          amount,
          currency,
          status: meta.status,
          description,
          customer_email: data.customerEmail || null,
          customer_name: data.customerName || null,
          payment_type: paymentMethod === "Cash" ? "cash" : meta.paymentType,
          review_link_included: watchIncludeReviewLink,
          provider: paymentMethod === "Cash" ? "cash" : meta.provider,
          url: "#",
        }]);
        if (paymentError) console.error("Error creating payment record:", paymentError);

        if (data.customerName) {
          await createOrUpdateCustomer(
            data.customerEmail || "",
            data.customerName,
            sharedPaymentId,
            user?.id,
            watchIncludeAddress ? data.street : undefined,
            watchIncludeAddress ? data.postcode : undefined,
            data.customerPhone || undefined,
            customerPaymentStatus,
            watchIncludeAddress ? data.city : undefined,
          );
        }

        toast({
          title: "Success",
          description: `${meta.label} sent via Email & SMS`,
        });

        form.reset({
          amount: "", currency: "GBP", description: "",
          customerEmail: "", customerPhone: "", customerName: "",
          includeAddress: false, includePdfReceipt: false,
          includeReviewLink: false, selectedReviewLink: "",
          street: "", city: "", postcode: "", country: "",
        });
        setWatchPaidWithCash(false);
        onLinkGenerated?.();
      } else if (hasEmail) {
        await handleSendPaymentEmail({ documentType: docType, includePdf, paymentMethod, customerPaymentStatus });
        setWatchPaidWithCash(false);
      } else {
        try {
          await handleSendPaymentSms({ documentType: docType, includePdf, paymentMethod, customerPaymentStatus });
          setWatchPaidWithCash(false);
        } catch (err: any) {
          if (err?.code === "sms_quota_exceeded" || err?.message === "sms_quota_exceeded") {
            const data = form.getValues();
            const canFallback = !!data.customerEmail;
            setPendingEmailFallback(canFallback ? () => async () => {
              await handleSendPaymentEmail({ documentType: docType, includePdf, paymentMethod, customerPaymentStatus });
              setWatchPaidWithCash(false);
            } : null);
            setQuotaDialogOpen(true);
            notifySmsQuotaExceeded({
              userId: user?.id,
              userEmail: user?.email,
              cycleEnd: quota?.cycle_end,
              businessName: businessProfile?.businessName,
            });
          } else {
            throw err;
          }
        }
      }
    } finally {
      setSendingType(null);
    }
  };

  return (
    <Card className="w-full" ref={formCardRef}>
      <CardHeader>
        <CardTitle>Create Invoice</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => { e.preventDefault(); onSend("invoice"); }} className="space-y-4 mt-6">
          <PaymentFormFields
            register={form.register}
            errors={form.formState.errors}
            setValue={form.setValue}
          />

          <CustomerDetailsForm
            form={form}
            includeAddress={watchIncludeAddress}
            onIncludeAddressChange={setWatchIncludeAddress}
            afterPhoneSlot={
              <PaymentOptionsForm
                register={form.register}
                watchIncludeReviewLink={watchIncludeReviewLink}
                setWatchIncludeReviewLink={setWatchIncludeReviewLink}
                watchIncludeAddress={watchIncludeAddress}
                setWatchIncludeAddress={setWatchIncludeAddress}
                watchIncludePdf={watchIncludePdf}
                setWatchIncludePdf={setWatchIncludePdf}
                watchPaidWithCash={watchPaidWithCash}
                setWatchPaidWithCash={setWatchPaidWithCash}
                handleReviewLinkSelected={handleReviewLinkSelected}
              />
            }
          />

          <div className="flex justify-end items-center gap-2">
            <SmsQuotaBadge />
            <Button variant="outline" size="sm" onClick={() => navigate("/account/subscription#sms-usage")}>
              Buy More
            </Button>
          </div>

          <PaymentActionButtons
            onSend={onSend}
            sendingType={sendingType}
            hasEmail={hasEmail}
            hasPhone={hasPhone}
            paidWithCash={watchPaidWithCash}
            includePdfInvoice={watchIncludePdf}
            includeReviewLink={watchIncludeReviewLink}
          />
        </form>
      </CardContent>
      <CardFooter />
      <SmsQuotaDialog
        open={quotaDialogOpen}
        onOpenChange={setQuotaDialogOpen}
        canFallbackToEmail={!!pendingEmailFallback}
        onSendByEmail={() => {
          const fn = pendingEmailFallback;
          setPendingEmailFallback(null);
          if (fn) {
            // pendingEmailFallback was stored via setState(() => callback),
            // so `fn` is the callback itself.
            (fn as unknown as () => Promise<void>)().catch((e) => console.error(e));
          }
        }}
      />
    </Card>
  );
};

export default PaymentLinkForm;
